import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepButton,
  LinearProgress,
  Paper,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { tokens } from "../../theme"; // Import your theme tokens
import Header from "../../components/Header";
import {
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Save as SaveIcon,
  InfoOutlined as InfoIcon,
  ErrorOutline as ErrorIcon,
} from "@mui/icons-material";

const DynamicForm = ({
  fields,
  steps = [],
  onSubmit,
  title,
  subtitle,
  initialValues,
  onFieldChange,
  reportTypes,
  validationSchema,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [activeStep, setActiveStep] = useState(0);
  const [dependentOptions, setDependentOptions] = useState({});
  const [completedSteps, setCompletedSteps] = useState(new Set());
  // Whether we've already tried to advance past the current step and failed —
  // used to show the step-level error summary without ambushing the user
  // before they've had a chance to fill anything in.
  const [showStepErrors, setShowStepErrors] = useState(false);

  useEffect(() => {
    setShowStepErrors(false);
  }, [activeStep]);

  const isMultiStep = Boolean(steps && steps.length > 0);

  // Calculate progress percentage
  const progressPercentage = isMultiStep
    ? ((activeStep + 1) / steps.length) * 100
    : 100;

  // Get current step fields
  const currentStepFields = isMultiStep
    ? (fields || []).filter((field) =>
        steps[activeStep]?.fields?.includes(field.name)
      )
    : (fields || []);

  const handleFieldChangeInternal = async (
    setFieldValue,
    field,
    value,
    values
  ) => {
    setFieldValue(field.name, value);

    if (field.parent === "main" && onFieldChange) {
      const options = await onFieldChange(field, value, setFieldValue);
      setDependentOptions(options || {});
    }

    if (field.onChange) {
      field.onChange(value, values, setFieldValue);
    }
  };

  const shouldDisableField = (field, values) => {
    if (field.disableCondition) {
      const disableField = fields.find(
        (f) => f.name === field.disableCondition.fieldName
      );
      return values[disableField?.name] === field.disableCondition.value;
    }
    return false;
  };

  const handleNext = async (values, validateForm, setTouched, touched) => {
    // Get a fresh, up-to-date errors object for the WHOLE form
    const formErrors = await validateForm();
    const currentStepFieldNames = steps[activeStep]?.fields || [];

    // Force every field on this step to "touched" so any error renders
    // inline immediately — don't make the user blur each field first.
    const touchedUpdates = currentStepFieldNames.reduce(
      (acc, name) => ({ ...acc, [name]: true }),
      {}
    );
    setTouched({ ...touched, ...touchedUpdates }, false);

    const isStepValid = currentStepFieldNames.every(
      (name) => !formErrors[name]
    );

    if (isStepValid) {
      setShowStepErrors(false);
      setCompletedSteps((prev) => new Set([...prev, activeStep]));
      setActiveStep((prevStep) => Math.min(prevStep + 1, steps.length - 1));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // Stay put, but now make it obvious why.
      setShowStepErrors(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeStep]);

  const handleBack = () => {
    setActiveStep((prevStep) => Math.max(prevStep - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleStepClick = (stepIndex) => {
    setActiveStep(stepIndex);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderField = (
    field,
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    setFieldValue,
    isSubmitting
  ) => {
    // Skip rendering if visibleCondition is false
    if (field.visibleCondition && !field.visibleCondition(values)) {
      return null;
    }

    const isDependent = field.dependentOn && !values[field.dependentOn];
    const isDisabled = shouldDisableField(field, values) || isSubmitting;
    const hasError = !!touched[field.name] && !!errors[field.name];
    const helperColor = hasError ? colors.redAccent[500] : colors.grey[300];

    const formatFileSelection = (value) => {
      if (!value) return "";

      const selectedFiles = Array.isArray(value) ? value : [value];
      const fileNames = selectedFiles
        .map((file) => file?.name || file)
        .filter(Boolean);

      return fileNames.join(", ");
    };

    if (field.type === "file") {
      const selectedFileNames = formatFileSelection(values[field.name]);

      return (
        <Box key={field.name} sx={{ gridColumn: field.gridColumn }}>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            disabled={isDisabled}
            sx={{
              height: 56,
              justifyContent: "flex-start",
              borderColor: hasError ? colors.redAccent[500] : colors.grey[400],
              color: colors.grey[100],
              "&:hover": {
                borderColor: colors.greenAccent[500],
                bgcolor: colors.primary[400],
              },
            }}
          >
            {selectedFileNames
              ? `Selected: ${selectedFileNames}`
              : field.label}
            <input
              type="file"
              style={{
                clip: 'rect(0 0 0 0)',
                clipPath: 'inset(50%)',
                height: 1,
                overflow: 'hidden',
                position: 'absolute',
                bottom: 0,
                left: 0,
                whiteSpace: 'nowrap',
                width: 1,
              }}
              accept={field.accept || field.inputProps?.accept}
              multiple={field.inputProps?.multiple}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                const fileValue = field.inputProps?.multiple
                  ? files
                  : files[0] || null;

                if (field.inputProps?.onChange) {
                  field.inputProps.onChange(e, fileValue);
                }

                setFieldValue(
                  field.name,
                  e.target.value ? fileValue : field.inputProps?.multiple ? [] : null
                );
              }}
            />
          </Button>
          {field.helperText && (
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                display: "block",
                color: colors.grey[300],
              }}
            >
              {field.helperText}
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Box key={field.name} sx={{ gridColumn: field.gridColumn }}>
        {field.type === "select" || field.type === "multiselect" ? (
          <Autocomplete
            disabled={isDependent || isDisabled}
            multiple={field.type === "multiselect"}
            options={
              dependentOptions[field.name] && dependentOptions[field.name].length > 0
                ? dependentOptions[field.name]
                : field.options || []
            }
            getOptionLabel={(option) => (option ? option.label : "")}
            {...field}
            onChange={(event, newValue) => {
              handleFieldChangeInternal(
                setFieldValue,
                field,
                field.type === "multiselect"
                  ? newValue.map((item) => item.value)
                  : newValue
                    ? newValue.value
                    : "",
                values
              );
            }}
            onBlur={handleBlur}
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                variant="filled"
                name={field.name}
                error={hasError}
                helperText={
                  (touched[field.name] && errors[field.name]) ||
                  field.helperText
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {params.InputProps.endAdornment}
                      {hasError && (
                        <ErrorIcon
                          fontSize="small"
                          sx={{ mr: 0.5, color: colors.redAccent[500] }}
                        />
                      )}
                      {field.tooltip && (
                        <Tooltip title={field.tooltip}>
                          <InfoIcon
                            fontSize="small"
                            sx={{ mr: 1, color: colors.grey[300] }}
                          />
                        </Tooltip>
                      )}
                    </>
                  ),
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    color: colors.grey[100],
                    ...(hasError && {
                      "& fieldset, &:before, &:after": {
                        borderColor: colors.redAccent[500],
                      },
                    }),
                  },
                  "& .MuiInputLabel-root": {
                    color: hasError ? colors.redAccent[500] : colors.grey[300],
                  },
                  "& .MuiFormHelperText-root": {
                    color: helperColor,
                    fontWeight: hasError ? 600 : 400,
                  },
                }}
              />
            )}
            value={(() => {
              let source = null;
              if (dependentOptions[field.name] && dependentOptions[field.name].length > 0) {
                source = dependentOptions[field.name];
              } else if (Array.isArray(field.options) && field.options.length > 0) {
                source = field.options;
              }

              if (!source) return field.type === "multiselect" ? [] : null;

              if (field.type === "multiselect") {
                return source.filter(
                  (option) => Array.isArray(values[field.name]) && values[field.name]?.includes(option.value)
                );
              }

              return source.find((option) => option.value === values[field.name]) || null;
            })()}
          />
        ) : field.type === "checkbox" ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={!!values[field.name]}
                onChange={(event) => {
                  handleFieldChangeInternal(
                    setFieldValue,
                    field,
                    event.target.checked,
                    values
                  );
                }}
                name={field.name}
                disabled={isDisabled}
                sx={{
                  color: colors.greenAccent[500],
                  "&.Mui-checked": {
                    color: colors.greenAccent[500],
                  },
                }}
              />
            }
            label={
              <Box display="flex" alignItems="center">
                <Typography sx={{ color: colors.grey[100] }}>
                  {field.label}
                </Typography>
                {field.tooltip && (
                  <Tooltip title={field.tooltip}>
                    <InfoIcon
                      fontSize="small"
                      sx={{ ml: 1, color: colors.grey[300] }}
                    />
                  </Tooltip>
                )}
              </Box>
            }
          />
        ) : (
          <TextField
            disabled={isDisabled}
            fullWidth
            variant="filled"
            type={field.type}
            label={field.label}
            onBlur={handleBlur}
            onChange={(e) => {
              handleChange(e);
              if (field.onChange) {
                field.onChange(e.target.value, values, setFieldValue);
              }
            }}
            value={values[field.name] || ""}
            name={field.name}
            error={hasError}
            helperText={
              (touched[field.name] && errors[field.name]) || field.helperText
            }
            InputProps={{
              endAdornment: (
                <>
                  {hasError && (
                    <ErrorIcon
                      fontSize="small"
                      sx={{ mr: field.tooltip ? 0.5 : 0, color: colors.redAccent[500] }}
                    />
                  )}
                  {field.tooltip && (
                    <Tooltip title={field.tooltip}>
                      <InfoIcon fontSize="small" sx={{ color: colors.grey[300] }} />
                    </Tooltip>
                  )}
                </>
              ),
            }}
            inputProps={{
              min: field.type === "number" ? field.min : undefined,
              max: field.type === "number" ? field.max : undefined,
              step: field.type === "number" ? field.step : undefined,
            }}
            InputLabelProps={
              field.type === "date" ? { shrink: true } : undefined
            }
            sx={{
              "& .MuiInputBase-root": {
                color: colors.grey[100],
                bgcolor: colors.primary[400],
                ...(hasError && {
                  "& fieldset, &:before, &:after": {
                    borderColor: colors.redAccent[500],
                  },
                }),
              },
              "& .MuiInputLabel-root": {
                color: hasError ? colors.redAccent[500] : colors.grey[300],
              },
              "& .MuiFormHelperText-root": {
                color: helperColor,
                fontWeight: hasError ? 600 : 400,
              },
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <Box m="20px">
      <Header title={title} subtitle={subtitle} />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          setTouched,
          isSubmitting,
          validateForm,
        }) => {
          // Fields on THIS step that currently have a visible error —
          // used both for the summary box and the nav-bar message.
          const stepErrorFields = currentStepFields.filter(
            (f) => errors[f.name] && (touched[f.name] || showStepErrors)
          );

          return (
            <form
              onSubmit={(e) => {
                if (isMultiStep && activeStep !== steps.length - 1) {
                  e.preventDefault();
                  return;
                }
                handleSubmit(e);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.target.type !== "submit") {
                  e.preventDefault();
                }
              }}
            >
              {/* Progress Bar (Only for multi-step forms) */}
              {isMultiStep && (
                <Paper
                  elevation={2}
                  sx={{
                    mb: 3,
                    overflow: "hidden",
                    bgcolor: colors.primary[400],
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{ color: colors.grey[100] }}
                      >
                        Step {activeStep + 1} of {steps.length}:{" "}
                        {steps[activeStep]?.stepLabel}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: colors.greenAccent[500] }}
                      >
                        {Math.round(progressPercentage)}% Complete
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={progressPercentage}
                      sx={{
                        height: 8,
                        borderRadius: 1,
                        bgcolor: colors.primary[500],
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 1,
                          bgcolor: colors.greenAccent[500],
                        },
                      }}
                    />
                    {steps[activeStep]?.stepDescription && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          color: colors.grey[300],
                        }}
                      >
                        {steps[activeStep].stepDescription}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              )}

              {/* Form Fields */}
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  mb: 3,
                  minHeight: isMultiStep ? 400 : undefined,
                  bgcolor: colors.primary[400],
                }}
              >
                {/* Step-level error summary — names the exact fields, not a vague notice */}
                {stepErrorFields.length > 0 && (
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      borderRadius: 1,
                      border: `1px solid ${colors.redAccent[500]}`,
                      bgcolor: `${colors.redAccent[500]}1a`,
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={0.5}>
                      <ErrorIcon
                        fontSize="small"
                        sx={{ color: colors.redAccent[500], mr: 1 }}
                      />
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{ color: colors.redAccent[500] }}
                      >
                        Please fix {stepErrorFields.length === 1 ? "this field" : `these ${stepErrorFields.length} fields`} before continuing:
                      </Typography>
                    </Box>
                    {stepErrorFields.map((f) => (
                      <Typography
                        key={f.name}
                        variant="body2"
                        sx={{ color: colors.redAccent[400], ml: 4 }}
                      >
                        • {f.label}: {errors[f.name]}
                      </Typography>
                    ))}
                  </Box>
                )}

                <Box
                  display="grid"
                  gap="24px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                >
                  {currentStepFields.map((field) =>
                    renderField(
                      field,
                      values,
                      errors,
                      touched,
                      handleBlur,
                      handleChange,
                      setFieldValue,
                      isSubmitting
                    )
                  )}
                </Box>
              </Paper>

              {/* Navigation Buttons */}
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: isMultiStep ? "space-between" : "flex-end",
                  alignItems: "center",
                  position: "sticky",
                  bottom: 0,
                  bgcolor: colors.primary[400],
                  zIndex: 10,
                }}
              >
                {isMultiStep && (
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    startIcon={<BackIcon />}
                    size="large"
                    type="button"
                    variant="outlined"
                    sx={{
                      borderColor: colors.grey[400],
                      color: colors.grey[100],
                      "&:hover": {
                        borderColor: colors.greenAccent[500],
                        bgcolor: colors.primary[500],
                      },
                      "&.Mui-disabled": {
                        borderColor: colors.grey[700],
                        color: colors.grey[600],
                      },
                    }}
                  >
                    Back
                  </Button>
                )}

                {isMultiStep && activeStep !== steps.length - 1 && (
                  <Button
                    onClick={() => handleNext(values, validateForm, setTouched, touched)}
                    type="button"
                    variant="contained"
                    size="large"
                    endIcon={<NextIcon />}
                    sx={{
                      minWidth: 150,
                      fontWeight: 600,
                      bgcolor: colors.greenAccent[500],
                      color: colors.grey[900],
                      "&:hover": {
                        bgcolor: colors.greenAccent[600],
                      },
                    }}
                  >
                    Next
                  </Button>
                )}

                {(!isMultiStep || activeStep === steps.length - 1) && (
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    size="large"
                    startIcon={
                      isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />
                    }
                    sx={{
                      minWidth: 150,
                      fontWeight: 600,
                      bgcolor: colors.greenAccent[500],
                      color: colors.grey[900],
                      "&:hover": {
                        bgcolor: colors.greenAccent[600],
                      },
                    }}
                  >
                    {isSubmitting ? "Saving..." : "Submit"}
                  </Button>
                )}
              </Paper>
            </form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default DynamicForm;